import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import FormField from '../common/FormField';
import { superAdminApi } from '../../services/superAdminApi';

const DomainConfigModal = ({ tenant, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    tenant_id: tenant?.id || '',
    domain_type: 'subdomain',
    domain_name: '',
    subdomain_suggestions: []
  });
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [subdomainAvailable, setSubdomainAvailable] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);

  useEffect(() => {
    fetchTenants();
  }, []);

  useEffect(() => {
    if (formData.domain_type === 'subdomain' && formData.domain_name) {
      checkSubdomainAvailability();
    }
  }, [formData.domain_name, formData.domain_type]);

  const fetchTenants = async () => {
    try {
      const response = await superAdminApi.getTenants({ status: 'active', per_page: 100 });
      console.log('Tenants API response:', response.data);
      
      // The API returns { success: true, data: { tenants: [], pagination: {} } }
      // So we need to access response.data.data.tenants
      if (response.data.data && response.data.data.tenants) {
        setTenants(response.data.data.tenants);
      } else if (response.data.tenants) {
        setTenants(response.data.tenants);
      } else {
        setTenants([]);
      }
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
      setTenants([]); // Ensure tenants is always an array
    }
  };

  const checkSubdomainAvailability = async () => {
    if (formData.domain_name.length < 2) {
      setSubdomainAvailable(null);
      return;
    }

    try {
      setCheckingAvailability(true);
      const response = await superAdminApi.checkSubdomainAvailability(formData.domain_name);
      setSubdomainAvailable(response.data.available);
      
      if (!response.data.available && response.data.suggestions) {
        setFormData(prev => ({
          ...prev,
          subdomain_suggestions: response.data.suggestions
        }));
      }
    } catch (error) {
      console.error('Failed to check subdomain availability:', error);
      setSubdomainAvailable(null);
    } finally {
      setCheckingAvailability(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'domain_type' && { subdomain_suggestions: [] })
    }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSuggestionClick = (suggestion) => {
    setFormData(prev => ({
      ...prev,
      domain_name: suggestion,
      subdomain_suggestions: []
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.tenant_id) {
      newErrors.tenant_id = 'Please select a tenant';
    }

    if (!formData.domain_name) {
      newErrors.domain_name = 'Domain name is required';
    } else if (formData.domain_type === 'subdomain') {
      if (formData.domain_name.length < 2) {
        newErrors.domain_name = 'Subdomain must be at least 2 characters';
      } else if (!/^[a-z0-9-]+$/.test(formData.domain_name)) {
        newErrors.domain_name = 'Subdomain can only contain lowercase letters, numbers, and hyphens';
      } else if (subdomainAvailable === false) {
        newErrors.domain_name = 'This subdomain is not available';
      }
    } else if (formData.domain_type === 'custom_domain') {
      const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?)*$/;
      if (!domainRegex.test(formData.domain_name)) {
        newErrors.domain_name = 'Please enter a valid domain name';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      await superAdminApi.createDomainConfiguration(formData);
      onSave();
    } catch (error) {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ general: error.response?.data?.message || 'Failed to create domain configuration' });
      }
    } finally {
      setLoading(false);
    }
  };

  const selectedTenant = tenants?.find(t => t.id === parseInt(formData.tenant_id));

  return (
    <Modal onClose={onClose} title="Add Domain Configuration" size="large">
      <form onSubmit={handleSubmit} className="domain-config-form">
        {errors.general && (
          <div className="error-message">
            {errors.general}
          </div>
        )}

        <div className="form-grid">
          <div className="form-field">
            <label className="field-label">
              Tenant<span className="required">*</span>
            </label>
            <select
              className="field-input"
              value={formData.tenant_id}
              onChange={(e) => handleInputChange('tenant_id', e.target.value)}
              disabled={!!tenant}
              required
            >
              <option value="">Select a tenant</option>
              {tenants.map(tenant => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name} ({tenant.slug})
                </option>
              ))}
            </select>
            {errors.tenant_id && (
              <span className="field-error">{errors.tenant_id}</span>
            )}
          </div>

          <div className="form-field">
            <label className="field-label">
              Domain Type<span className="required">*</span>
            </label>
            <select
              className="field-input"
              value={formData.domain_type}
              onChange={(e) => handleInputChange('domain_type', e.target.value)}
              required
            >
              <option value="subdomain">Subdomain</option>
              <option value="custom_domain">Custom Domain</option>
            </select>
            {errors.domain_type && (
              <span className="field-error">{errors.domain_type}</span>
            )}
          </div>
        </div>

        <div className="form-field">
          <label className="field-label">
            {formData.domain_type === 'subdomain' ? 'Subdomain' : 'Custom Domain'}
            <span className="required">*</span>
          </label>
          <div className="domain-input-container">
            <input
              type="text"
              className="field-input"
              value={formData.domain_name}
              onChange={(e) => handleInputChange('domain_name', e.target.value.toLowerCase())}
              placeholder={formData.domain_type === 'subdomain' ? 'my-organization' : 'example.com'}
              required
            />
            
            {formData.domain_type === 'subdomain' && (
              <span className="domain-suffix">.abfisaas.com</span>
            )}
            
            {formData.domain_type === 'subdomain' && formData.domain_name && (
              <div className="availability-status">
                {checkingAvailability ? (
                  <span className="checking">
                    <i className="fas fa-spinner fa-spin"></i>
                    Checking...
                  </span>
                ) : subdomainAvailable === true ? (
                  <span className="available">
                    <i className="fas fa-check"></i>
                    Available
                  </span>
                ) : subdomainAvailable === false ? (
                  <span className="unavailable">
                    <i className="fas fa-times"></i>
                    Not Available
                  </span>
                ) : null}
              </div>
            )}
          </div>
          {errors.domain_name && (
            <span className="field-error">{errors.domain_name}</span>
          )}
        </div>

        {formData.subdomain_suggestions.length > 0 && (
          <div className="suggestions-section">
            <label>Suggested alternatives:</label>
            <div className="suggestions-list">
              {formData.subdomain_suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  type="button"
                  className="suggestion-button"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion}.abfisaas.com
                </button>
              ))}
            </div>
          </div>
        )}

        {formData.tenant_id && formData.domain_name && (
          <div className="preview-section">
            <h4>Configuration Preview</h4>
            <div className="preview-content">
              <div className="preview-item">
                <label>Tenant:</label>
                <span>{selectedTenant?.name}</span>
              </div>
              <div className="preview-item">
                <label>Domain:</label>
                <span className="domain-preview">
                  {formData.domain_type === 'subdomain' 
                    ? `${formData.domain_name}.abfisaas.com`
                    : formData.domain_name
                  }
                </span>
              </div>
              <div className="preview-item">
                <label>Type:</label>
                <span>{formData.domain_type === 'subdomain' ? 'Subdomain' : 'Custom Domain'}</span>
              </div>
            </div>
          </div>
        )}

        {formData.domain_type === 'custom_domain' && (
          <div className="info-section">
            <div className="info-message">
              <i className="fas fa-info-circle"></i>
              <div>
                <strong>Custom Domain Setup</strong>
                <p>After creating this configuration, you'll need to update your domain's DNS records to point to our servers. DNS instructions will be provided after creation.</p>
              </div>
            </div>
          </div>
        )}

        <div className="modal-actions">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || (formData.domain_type === 'subdomain' && subdomainAvailable === false)}
            className="btn-primary"
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Creating...
              </>
            ) : (
              'Create Domain Configuration'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default DomainConfigModal;